/**
 * Asset Cache Type Definitions
 * Centralized type definitions for the entire asset cache system
 */

// ============================================================================
// CORE ASSET TYPES
// ============================================================================

/**
 * Asset types that can be cached
 */
export enum AssetType {
  IMAGE = "image",
  VIDEO = "video",
  FONT = "font",
  AUDIO = "audio",
  OTHER = "other",
}

// ============================================================================
// CACHE OPERATION TYPES
// ============================================================================

/**
 * Options for asset cache operations
 */
export interface CacheOptions {
  ttl?: number; // Time to live in seconds
  revalidate?: boolean; // Whether to revalidate on access
  tags?: string[]; // Cache tags for invalidation
  priority?: "high" | "medium" | "low"; // Priority for preloading
}

/**
 * Cache entry with metadata
 */
export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expires?: number;
  type: AssetType;
  tags?: string[];
  version?: string;
}

/**
 * Result of a cache operation
 */
export interface CacheResult<T> {
  success: boolean;
  data?: T;
  fromCache: boolean;
  error?: Error;
}

/**
 * Asset metadata for cache manifest
 */
export interface AssetMetadata {
  url: string;
  type: AssetType;
  version: string;
  lastModified: string;
  size?: number;
  tags?: string[];
}

/**
 * Cache manifest structure
 */
export interface CacheManifest {
  version: string;
  generatedAt: string;
  assets: Record<string, AssetMetadata>;
}

// ============================================================================
// SERVICE WORKER TYPES
// ============================================================================

/**
 * Service worker message types
 */
export enum ServiceWorkerMessageType {
  CACHE_ASSET = "CACHE_ASSET",
  INVALIDATE_ASSET = "INVALIDATE_ASSET",
  INVALIDATE_TAG = "INVALIDATE_TAG",
  CLEAR_CACHE = "CLEAR_CACHE",
  UPDATE_MANIFEST = "UPDATE_MANIFEST",
  CHECK_ASSET = "CHECK_ASSET",
}

// Service Worker Global Scope Types (simplified to avoid DOM conflicts)
export interface ExtendableEvent extends Event {
  waitUntil(promise: Promise<any>): void;
}

export interface FetchEvent extends ExtendableEvent {
  request: Request;
  respondWith(response: Promise<Response> | Response): void;
}

export interface ExtendableMessageEvent extends ExtendableEvent {
  data: any;
  source: Client | MessagePort | ServiceWorker | null;
}

export interface ServiceWorkerGlobalScope {
  caches: CacheStorage;
  clients: Clients;
  registration: ServiceWorkerRegistration;
  location: Location;
  addEventListener(
    type: "install",
    listener: (event: ExtendableEvent) => void
  ): void;
  addEventListener(
    type: "activate",
    listener: (event: ExtendableEvent) => void
  ): void;
  addEventListener(type: "fetch", listener: (event: FetchEvent) => void): void;
  addEventListener(
    type: "message",
    listener: (event: ExtendableMessageEvent) => void
  ): void;
  skipWaiting(): Promise<void>;
}

export interface Client {
  id: string;
  type: "window" | "worker" | "sharedworker";
  url: string;
  postMessage(message: any, transfer?: Transferable[]): void;
}

export interface Clients {
  get(id: string): Promise<Client | undefined>;
  matchAll(options?: ClientQueryOptions): Promise<Client[]>;
  openWindow(url: string): Promise<WindowClient | null>;
  claim(): Promise<void>;
}

export interface ClientQueryOptions {
  includeUncontrolled?: boolean;
  type?: "window" | "worker" | "sharedworker" | "all";
}

export interface WindowClient extends Client {
  focused: boolean;
  visibilityState: "hidden" | "visible" | "prerender" | "unloaded";
  focus(): Promise<WindowClient>;
  navigate(url: string): Promise<WindowClient | null>;
}

// ============================================================================
// CONFIGURATION TYPES
// ============================================================================

/**
 * Asset Cache Configuration Interface
 * Simplified configuration interface containing only implemented features
 */
export interface AssetCacheConfig {
  /** Enable or disable the asset cache system */
  enabled?: boolean;
  /** Cache version for invalidation */
  version?: string;
  /** Enable debug logging */
  debug?: boolean;
  /**
   * Name of the cache storage (used with browser Cache API)
   * Should be a unique identifier for your application's cache
   * Examples: "my-app-cache-v1", "storefront-assets", "asset-cache-prod"
   */
  storageName?: string;
  /** Maximum cache size in MB */
  maxSize?: number;
  /** Maximum age for cached assets in seconds */
  maxAge?: number;
  /** Enable service worker for caching */
  serviceWorkerEnabled?: boolean;
  /** Service worker file path */
  serviceWorkerPath?: string;
  /** Cache manifest file path */
  manifestPath?: string;
}

/**
 * Asset loader configuration
 */
export interface AssetLoaderConfig {
  cacheName?: string;
  defaultTTL?: number;
  maxSize?: number; // Maximum size in bytes for cacheable assets
  enableServiceWorker?: boolean;
  manifestPath?: string;
  debug?: boolean;
}
